// useSmsTriggers — Trigger Library state, ported from the prototype's 'triggers'
// route. The three editable triggers (batch1 / batch2 / followup) are seeded
// from TRIGGER_DEFAULTS; "Save as draft" reflects the change locally so the
// display updates (mock — no backend, same as the standalone build).
import { reactive, ref, computed } from "vue";
import * as SMS from "../sms-config";

export function useSmsTriggers() {
  // ── Editable triggers ─────────────────────────────────────────────────────
  const triggers = reactive({
    batch1: { ...SMS.TRIGGER_DEFAULTS.batch1 },
    batch2: { ...SMS.TRIGGER_DEFAULTS.batch2 },
    followup: { ...SMS.TRIGGER_DEFAULTS.followup }
  });

  const staticTriggers = SMS.STATIC_TRIGGERS;
  const triggerChangelog = SMS.TRIGGER_CHANGELOG;

  // ── Edit modal ────────────────────────────────────────────────────────────
  const modal = ref(null); // null | 'trigger'
  const triggerModal = reactive({
    key: null,
    title: "",
    currentVal: "",
    newTime: "",
    newDay: "day-before",
    reason: ""
  });

  // Display label for an editable trigger.
  function triggerDisplay(key) {
    const t = triggers[key];
    if (key === "followup") {
      const [h, m] = t.time.split(":");
      const hr = parseInt(h, 10);
      const ampm = hr >= 12 ? "PM" : "AM";
      const hr12 = hr % 12 || 12;
      return `${hr12}:${m} ${ampm} · 3 days after fulfilled · local time`;
    }
    return SMS.formatTriggerTime(t.time, t.day);
  }

  // "Other triggers" table rows (ETA / Delivered static, Follow-up editable).
  const otherTriggers = computed(() => {
    const st = SMS.STATIC_TRIGGERS;
    return [
      {
        tag: "ETA",
        applies: "Callia Driver only",
        label: "Fires when",
        fires: st.eta.fires,
        note: st.eta.note,
        source: st.eta.source,
        editable: false
      },
      {
        tag: "Delivered",
        applies: "Driver & Courier",
        label: "Fires when",
        fires: st.delivered.fires,
        note: st.delivered.note,
        source: st.delivered.source,
        editable: false
      },
      {
        tag: "Follow-up",
        applies: "Driver & Courier",
        label: "Sends at",
        fires: triggerDisplay("followup"),
        note: "Offset from Shopify fulfillment timestamp",
        source: "⚙️ System trigger",
        editable: true
      }
    ];
  });

  function openTriggerEdit(key) {
    const t = triggers[key];
    triggerModal.key = key;
    triggerModal.title = SMS.TRIGGER_DEFAULTS[key].modalTitle;
    triggerModal.currentVal = triggerDisplay(key);
    triggerModal.newTime = t.time;
    triggerModal.newDay = t.day;
    triggerModal.reason = "";
    modal.value = "trigger";
  }

  function closeModal() {
    modal.value = null;
  }

  function saveTriggerDraft() {
    const k = triggerModal.key;
    if (!k || !triggerModal.reason.trim()) return;
    // Mock prototype: reflect the change locally so the display updates.
    triggers[k].time = triggerModal.newTime;
    triggers[k].day = triggerModal.newDay;
    closeModal();
  }

  return {
    triggers,
    staticTriggers,
    triggerChangelog,
    otherTriggers,
    modal,
    triggerModal,
    triggerDisplay,
    openTriggerEdit,
    saveTriggerDraft,
    closeModal
  };
}
